import type { PriorityLevelGroup } from '@integratio/core';
import { formatPercent } from '../../lib/format';
import { fitView, pointsAttr, project, shade, slab, type Point } from './iso';

interface Props {
  groups: PriorityLevelGroup[];
  total: number;
  print?: boolean;
}

const FOOTPRINT = 2.5;
const STACK_HEIGHT = 3.1;
const MIN_SEGMENT = 0.12;
const THICKNESS = 0.24;
const PAD = 0.45;

/** Ordem de empilhamento (base -> topo). */
const STACK_ORDER = ['OUTRAS', 'BAIXA', 'MÉDIA', 'ALTA'];

function IsoStack({ groups, width, height }: { groups: PriorityLevelGroup[]; width: number; height: number }): JSX.Element {
  const gridWidth = FOOTPRINT + PAD * 2;
  const gridDepth = FOOTPRINT + PAD * 2;
  const view = fitView({
    svgWidth: width,
    svgHeight: height,
    gridWidth,
    gridDepth,
    maxHeight: STACK_HEIGHT,
    thickness: THICKNESS,
    paddingX: 190,
    paddingY: 40,
  });

  const platform = slab(gridWidth, gridDepth, THICKNESS, view);
  const x0 = PAD;
  const x1 = PAD + FOOTPRINT;
  const y0 = PAD;
  const y1 = PAD + FOOTPRINT;

  const ordered = [...groups].sort(
    (a, b) => STACK_ORDER.indexOf(a.level) - STACK_ORDER.indexOf(b.level),
  );
  const visible = ordered.filter((group) => group.count > 0);
  const totalPercentage = visible.reduce((acc, group) => acc + group.percentage, 0) || 1;

  let cursor = 0;
  const segments = visible.map((group) => {
    const segmentHeight = Math.max(MIN_SEGMENT, (group.percentage / totalPercentage) * STACK_HEIGHT);
    const zBottom = cursor;
    const zTop = cursor + segmentHeight;
    cursor = zTop;
    const p = (x: number, y: number, z: number): Point => project(x, y, z, view);
    return {
      group,
      zTop,
      faces: {
        top: [p(x0, y0, zTop), p(x1, y0, zTop), p(x1, y1, zTop), p(x0, y1, zTop)],
        left: [p(x0, y1, zTop), p(x1, y1, zTop), p(x1, y1, zBottom), p(x0, y1, zBottom)],
        right: [p(x1, y0, zTop), p(x1, y1, zTop), p(x1, y1, zBottom), p(x1, y0, zBottom)],
      },
      /** Ponto médio da face direita, ponto de partida da linha-guia. */
      anchor: p(x1, (y0 + y1) / 2, (zBottom + zTop) / 2),
    };
  });

  const labelX = width - 150;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Prioridade de atuação" className="h-auto w-full">
      <defs>
        <linearGradient id="priority-slab" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F2F2F5" />
          <stop offset="100%" stopColor="#E3E3E8" />
        </linearGradient>
        <filter id="priority-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="12" stdDeviation="14" floodColor="#1B1A20" floodOpacity="0.18" />
        </filter>
      </defs>

      <g filter="url(#priority-shadow)">
        <polygon points={pointsAttr(platform.left)} fill="#CFCFD6" />
        <polygon points={pointsAttr(platform.right)} fill="#BFBFC7" />
        <polygon points={pointsAttr(platform.top)} fill="url(#priority-slab)" />
      </g>

      {segments.map(({ group, faces }) => (
        <g key={group.key}>
          <polygon points={pointsAttr(faces.left)} fill={shade(group.color, 0.74)} />
          <polygon points={pointsAttr(faces.right)} fill={shade(group.color, 0.56)} />
          <polygon points={pointsAttr(faces.top)} fill={group.color} stroke="rgba(255,255,255,0.35)" />
        </g>
      ))}

      {segments.map(({ group, anchor }, index) => {
        const labelY = anchor[1] + 2 + index * 2;
        return (
          <g key={`label-${group.key}`}>
            <polyline
              points={pointsAttr([
                [anchor[0], anchor[1]],
                [labelX - 18, labelY],
              ])}
              fill="none"
              stroke={group.color}
              strokeWidth="1.4"
            />
            <circle cx={anchor[0]} cy={anchor[1]} r="4" fill={group.color} />
            <text x={labelX} y={labelY + 16} fontSize="13" fontWeight="700" fill="#54525E" fontFamily="inherit">
              {group.name} · {group.count} stakeholder{group.count === 1 ? '' : 's'}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function PriorityChart({ groups, total, print = false }: Props): JSX.Element {
  return (
    <div className={print ? 'grid grid-cols-[360px_1fr] gap-8' : 'grid gap-8 xl:grid-cols-[340px_1fr]'}>
      {/* Legenda por nível e subgrupo de atuação */}
      <div className="relative pl-6">
        <span aria-hidden className="absolute inset-y-1 left-1.5 w-px bg-brand-line" />
        <ul className="space-y-5">
          {groups.map((group) => (
            <li key={group.key} className="avoid-break relative">
              <span
                aria-hidden
                className="absolute -left-6 top-1 h-3 w-3 rounded-full ring-4 ring-white"
                style={{ backgroundColor: group.color }}
              />
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p
                  className="text-[12px] font-bold uppercase tracking-wide"
                  style={{ color: group.color }}
                >
                  Prioridade {group.name}
                </p>
                <p className="text-[11px] font-semibold tabular-nums text-brand-slate">
                  {formatPercent(group.percentage)} · {group.count} de {total}
                </p>
              </div>

              {group.count === 0 ? (
                <p className="mt-1 text-[11px] italic text-brand-slate/80">{group.emptyText}</p>
              ) : (
                <div className="mt-2 space-y-3">
                  {group.subgroups.map((subgroup) => (
                    <div key={subgroup.key} className="avoid-break">
                      <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: subgroup.color }}>
                        {subgroup.name}
                        <span className="ml-1.5 font-semibold text-brand-slate">
                          {formatPercent(subgroup.percentage)} · {subgroup.count}
                        </span>
                      </p>
                      {print ? null : (
                        <ul className="mt-1 space-y-0.5">
                          {subgroup.stakeholders.slice(0, 8).map((item) => (
                            <li key={item.id} className="text-[11px] leading-snug" style={{ color: subgroup.color }}>
                              <span className="font-semibold">{item.nome}</span>
                              {(item.cargo || item.empresaInstituicao) && (
                                <span className="block text-[9px] text-brand-slate/80">
                                  {[item.cargo, item.empresaInstituicao].filter(Boolean).join(' — ')}
                                </span>
                              )}
                            </li>
                          ))}
                          {subgroup.stakeholders.length > 8 && (
                            <li className="text-[11px] font-semibold text-brand-slate">
                              + {subgroup.stakeholders.length - 8} stakeholder(s)
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="min-w-0">
        <IsoStack groups={groups} width={1000} height={print ? 480 : 580} />
      </div>
    </div>
  );
}
